import React, { useEffect, useState } from "react";
import { Download, X, Bookmark, Tv } from "lucide-react";

const DISMISSED_KEY = "tapin_pwa_dismissed";
const INSTALLED_KEY = "tapin_pwa_installed";

/**
 * PwaInstallPrompt — multi-browser install/bookmark prompt.
 *
 * 1. Chromium/Android/desktop → captures `beforeinstallprompt` and exposes
 *    a one-click install.
 * 2. iOS Safari → shows a "Bookmark to Home Screen" guide (the event above
 *    never fires on iOS).
 * 3. Smart-TV browsers (Samsung Tizen / LG webOS / Fire TV / Android TV)
 *    → shows the pretty path: "Press ☆ or + and bookmark to Home".
 *
 * Hides itself on the `/tv` route (TV app launches itself full-screen) and
 * when the user has dismissed or already installed.
 */
export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [flavor, setFlavor] = useState("android"); // android | ios | tv

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;
    if (localStorage.getItem(INSTALLED_KEY)) return;
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/tv")) return;

    // Already installed to home screen (standalone mode) → hide
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = (navigator.userAgent || "").toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua); // iOS Safari only
    const isTv =
      /smart-?tv|smarttv|webos|tizen|hbbtv|viera|googletv|bravia|appletv|ft[a-z]v/.test(ua) ||
      (window.innerWidth >= 1600 && !("ontouchstart" in window) && /crkey|armv7|linux arm|android tv|fire tv|firetv/.test(ua));

    if (isTv) {
      setFlavor("tv");
      // Slight delay so it doesn't pop immediately on cold-boot
      const t = setTimeout(() => setShow(true), 5000);
      return () => clearTimeout(t);
    }
    if (isIOS) {
      setFlavor("ios");
      const t = setTimeout(() => setShow(true), 4000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setFlavor("android");
      setShow(true);
    };
    const installed = () => {
      localStorage.setItem(INSTALLED_KEY, "1");
      setShow(false);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") localStorage.setItem(INSTALLED_KEY, "1");
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  if (!show) return null;

  // TV flavor — "bookmark to home" instructions, biggest text, most breathing room
  if (flavor === "tv") {
    return (
      <div
        data-testid="pwa-install-prompt-tv"
        className="fixed bottom-8 right-8 z-[60] tv-glass rounded-3xl p-6 max-w-md border-2 border-tv-accent/50 shadow-2xl"
      >
        <button onClick={dismiss} data-testid="pwa-install-dismiss" className="absolute top-3 right-3 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-tv-accent/20 flex items-center justify-center shrink-0">
            <Tv className="w-7 h-7 text-tv-accent" />
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-white text-xl">Add to TV Home</div>
            <div className="text-sm text-white/70 mt-1">Your TV's app store doesn't have us yet. Use your remote's <strong className="text-tv-accent">Bookmark</strong> / <strong className="text-tv-accent">★</strong> / <strong className="text-tv-accent">Add to Home</strong> button to keep Tap IN TV on your home screen forever.</div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <a
                href="/tv"
                data-testid="pwa-install-tv-launch"
                className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-5 py-2 rounded-full text-sm"
              >
                Launch TV App →
              </a>
              <button onClick={dismiss} className="text-white/60 px-3 py-2 text-sm">Not now</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // iOS flavor — Safari doesn't expose install API. Teach the user the Share → Add to Home gesture.
  if (flavor === "ios") {
    return (
      <div
        data-testid="pwa-install-prompt-ios"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-5 md:max-w-sm z-50 tv-glass rounded-2xl p-4 border border-tv-accent/40 shadow-2xl"
      >
        <button onClick={dismiss} data-testid="pwa-install-dismiss" className="absolute top-2 right-2 text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-tv-accent/20 flex items-center justify-center shrink-0">
            <Bookmark className="w-5 h-5 text-tv-accent" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold text-white">Install Tap IN TV</div>
            <div className="text-xs text-white/70 mt-1">Tap the <span className="inline-block w-5 h-5 bg-white/10 rounded text-center leading-5 font-bold">⬆</span> Share icon below, then <strong>Add to Home Screen</strong>.</div>
          </div>
        </div>
      </div>
    );
  }

  // Android / desktop Chromium — native install prompt
  return (
    <div data-testid="pwa-install-prompt" className="fixed bottom-5 right-5 z-50 tv-glass rounded-2xl p-4 max-w-sm border border-tv-accent/40 shadow-2xl">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-tv-accent/20 flex items-center justify-center">
          <Download className="w-5 h-5 text-tv-accent" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-white">Install Tap In TV</div>
          <div className="text-xs text-white/70 mt-1">One tap to watch anywhere — faster, full-screen, offline-friendly.</div>
          <div className="flex gap-2 mt-3">
            <button data-testid="pwa-install-btn" onClick={install} className="bg-tv-accent text-black font-bold px-4 py-1.5 rounded-full text-sm">Install</button>
            <button data-testid="pwa-install-dismiss" onClick={dismiss} className="text-white/60 px-3 py-1.5 text-sm">Not now</button>
          </div>
        </div>
      </div>
    </div>
  );
}
